import React, { useRef, useEffect } from 'react';
import { Bold, Italic, Underline, List, ListOrdered, Link2, AlignLeft, AlignCenter, AlignRight, Heading1, Heading2, Heading3, Undo, Redo } from 'lucide-react';
import { Button } from './ui/button';

const SimpleRichTextEditor = ({ value, onChange, placeholder }) => {
  const editorRef = useRef(null);

  useEffect(() => {
    if (editorRef.current && editorRef.current.innerHTML !== value) {
      editorRef.current.innerHTML = value || '';
    }
  }, [value]);

  const execCommand = (command, value = null) => {
    document.execCommand(command, false, value);
    handleInput();
    editorRef.current?.focus();
  };

  const handleInput = () => {
    if (editorRef.current) {
      onChange(editorRef.current.innerHTML);
    }
  };

  const handleLink = () => {
    const url = prompt('Enter URL:');
    if (url) {
      execCommand('createLink', url);
    }
  };

  const ToolbarButton = ({ onClick, icon: Icon, title, active }) => (
    <Button
      type="button"
      variant="ghost"
      size="sm"
      onClick={onClick}
      title={title}
      className={`h-8 w-8 p-0 ${active ? 'bg-gray-200 dark:bg-gray-700' : 'hover:bg-gray-100 dark:hover:bg-gray-800'}`}
    >
      <Icon className="w-4 h-4" />
    </Button>
  );

  return (
    <div className="border-2 border-gray-300 dark:border-gray-600 rounded-lg overflow-hidden">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-1 p-2 bg-gray-50 dark:bg-[#252525] border-b border-gray-300 dark:border-gray-600">
        {/* Undo/Redo */}
        <ToolbarButton onClick={() => execCommand('undo')} icon={Undo} title="Undo" />
        <ToolbarButton onClick={() => execCommand('redo')} icon={Redo} title="Redo" />
        
        <div className="w-px h-6 bg-gray-300 dark:bg-gray-600 mx-1" />
        
        {/* Headings */}
        <ToolbarButton onClick={() => execCommand('formatBlock', 'h1')} icon={Heading1} title="Heading 1" />
        <ToolbarButton onClick={() => execCommand('formatBlock', 'h2')} icon={Heading2} title="Heading 2" />
        <ToolbarButton onClick={() => execCommand('formatBlock', 'h3')} icon={Heading3} title="Heading 3" />
        
        <div className="w-px h-6 bg-gray-300 dark:bg-gray-600 mx-1" />
        
        {/* Text Formatting */}
        <ToolbarButton onClick={() => execCommand('bold')} icon={Bold} title="Bold" />
        <ToolbarButton onClick={() => execCommand('italic')} icon={Italic} title="Italic" />
        <ToolbarButton onClick={() => execCommand('underline')} icon={Underline} title="Underline" />
        
        <div className="w-px h-6 bg-gray-300 dark:bg-gray-600 mx-1" />
        
        {/* Lists */}
        <ToolbarButton onClick={() => execCommand('insertUnorderedList')} icon={List} title="Bullet List" />
        <ToolbarButton onClick={() => execCommand('insertOrderedList')} icon={ListOrdered} title="Numbered List" />
        
        <div className="w-px h-6 bg-gray-300 dark:bg-gray-600 mx-1" />
        
        {/* Alignment */}
        <ToolbarButton onClick={() => execCommand('justifyLeft')} icon={AlignLeft} title="Align Left" />
        <ToolbarButton onClick={() => execCommand('justifyCenter')} icon={AlignCenter} title="Align Center" />
        <ToolbarButton onClick={() => execCommand('justifyRight')} icon={AlignRight} title="Align Right" />
        
        <div className="w-px h-6 bg-gray-300 dark:bg-gray-600 mx-1" />
        
        {/* Link */}
        <ToolbarButton onClick={handleLink} icon={Link2} title="Insert Link" />
      </div>

      {/* Editor */}
      <div
        ref={editorRef}
        contentEditable
        onInput={handleInput}
        className="min-h-[350px] p-4 focus:outline-none bg-white dark:bg-[#1a1a1a] text-gray-700 dark:text-gray-300 prose prose-sm max-w-none"
        style={{ 
          lineHeight: '1.75',
          overflowY: 'auto',
          maxHeight: '400px'
        }}
        data-placeholder={placeholder}
        suppressContentEditableWarning={true}
      />

      <style>{`
        [contentEditable=true]:empty:before {
          content: attr(data-placeholder);
          color: #9ca3af;
          pointer-events: none;
        }
        [contentEditable=true] h1 {
          font-size: 1.5rem;
          font-weight: 700;
          margin: 1rem 0 0.5rem 0;
        }
        [contentEditable=true] h2 {
          font-size: 1.25rem;
          font-weight: 600;
          margin: 0.875rem 0 0.5rem 0;
        }
        [contentEditable=true] h3 {
          font-size: 1.125rem;
          font-weight: 600;
          margin: 0.75rem 0 0.375rem 0;
        }
        [contentEditable=true] p {
          margin-bottom: 0.75rem;
        }
        [contentEditable=true] ul,
        [contentEditable=true] ol {
          padding-left: 1.5rem;
          margin-bottom: 0.75rem;
        }
        [contentEditable=true] li {
          margin-bottom: 0.25rem;
        }
        [contentEditable=true] a {
          color: #FFD12D;
          text-decoration: underline;
        }
      `}</style>
    </div>
  );
};

export default SimpleRichTextEditor;
